import { html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { SsoElement } from './sso-base.js';

export type SsoAlertKind = 'info' | 'success' | 'warning' | 'error';

/**
 * `<sso-alert>` — inline status / error banner with an aria-live region (§5.4).
 *
 * `error` and `warning` use `aria-live="assertive"` so failures are announced immediately;
 * `info`/`success` use `polite`. Content is the default slot.
 */
@customElement('sso-alert')
export class SsoAlert extends SsoElement {
  static override styles = [
    SsoElement.styles,
    css`
      :host {
        display: block;
        margin-bottom: var(--sso-space);
      }
      .alert {
        display: flex;
        gap: 0.6rem;
        align-items: flex-start;
        padding: 0.7rem 0.9rem;
        border-radius: var(--sso-radius);
        border: 1px solid;
        font-size: 0.95rem;
        line-height: 1.4;
      }
      .icon {
        flex: none;
        font-size: 1.1rem;
        line-height: 1.3;
      }
      :host([kind='info']) .alert {
        background: color-mix(in srgb, var(--sso-color-brand) 8%, var(--sso-color-surface));
        border-color: color-mix(in srgb, var(--sso-color-brand) 40%, transparent);
        color: var(--sso-color-fg);
      }
      :host([kind='success']) .alert {
        background: color-mix(in srgb, var(--sso-color-success) 10%, var(--sso-color-surface));
        border-color: color-mix(in srgb, var(--sso-color-success) 45%, transparent);
        color: var(--sso-color-success);
      }
      :host([kind='warning']) .alert {
        background: color-mix(in srgb, var(--sso-color-warning) 12%, var(--sso-color-surface));
        border-color: color-mix(in srgb, var(--sso-color-warning) 45%, transparent);
        color: var(--sso-color-warning);
      }
      :host([kind='error']) .alert {
        background: color-mix(in srgb, var(--sso-color-danger) 8%, var(--sso-color-surface));
        border-color: color-mix(in srgb, var(--sso-color-danger) 45%, transparent);
        color: var(--sso-color-danger);
      }
    `,
  ];

  @property({ reflect: true }) kind: SsoAlertKind = 'info';

  private get icon(): string {
    switch (this.kind) {
      case 'success':
        return '✓';
      case 'warning':
        return '⚠';
      case 'error':
        return '⚠';
      default:
        return 'ℹ';
    }
  }

  override render() {
    const assertive = this.kind === 'error' || this.kind === 'warning';
    return html`
      <div class="alert" role=${assertive ? 'alert' : 'status'} aria-live=${assertive ? 'assertive' : 'polite'}>
        <span class="icon" aria-hidden="true">${this.icon}</span>
        <span><slot></slot></span>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sso-alert': SsoAlert;
  }
}
